import Breadcrumb from '@/components/feature/Breadcrumb';
import { useState } from 'react';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于六合宝典',
  description:
    '六合宝典是玩家必备的权威参考工具，致力于提供最全面的六合彩玩法解析、历史数据和走势分析。',
  url: 'https://liuhebaodian.com/about',
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>(
    'idle'
  );
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (
      form.querySelector('[name="phone_alt"]') as HTMLInputElement
    )?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    const formData = new FormData(form);
    formData.delete('phone_alt');

    try {
      const res = await fetch(
        'https://readdy.ai/api/form/d9rh3b4nlsngrm4lmc2g',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams(
            formData as unknown as Record<string, string>
          ).toString(),
        }
      );
      const text = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(text);
      } catch {
        // ignore
      }
      const code =
        parsed && typeof parsed === 'object' && 'code' in parsed
          ? (parsed as Record<string, string>).code
          : '';
      if (res.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg =
          parsed && typeof parsed === 'object' && 'meta' in parsed
            ? (parsed as Record<string, Record<string, string>>).meta?.message
            : '';
        setFormError(msg || '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后再试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <title>关于我们 | 六合宝典</title>
      <meta
        name="description"
        content="了解六合宝典的品牌故事、专业信誉与联系方式。我们致力于为玩家提供最权威的六合彩信息参考服务。"
      />
      <meta name="keywords" content="六合宝典关于我们,六合宝典品牌,六合宝典联系方式" />
      <link rel="canonical" href="https://liuhebaodian.com/about" />
      <meta property="og:title" content="关于我们 | 六合宝典" />
      <meta
        property="og:description"
        content="了解六合宝典的品牌故事、专业信誉与联系方式。"
      />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://liuhebaodian.com/about" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(aboutSchema),
        }}
      />

      <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

        {/* Page Header */}
        <div className="mt-6 mb-12">
          <h1 className="font-heading text-4xl md:text-5xl text-foreground-900 mb-4">
            关于六合宝典
          </h1>
          <p className="text-lg text-foreground-600 max-w-2xl">
            专业、权威、可靠——我们致力于为每一位玩家提供最优质的六合彩信息参考服务。
          </p>
        </div>

        {/* Brand Story */}
        <section className="mb-16">
          <h2 className="font-heading text-2xl text-foreground-900 mb-6">
            品牌故事
          </h2>
          <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70">
            <p className="text-foreground-700 leading-relaxed mb-4">
              六合宝典诞生于对六合彩信息透明化的追求。我们的创始团队由资深的数据分析师和彩票行业专家组成，深知玩家在面对海量信息时的困惑与需求。
            </p>
            <p className="text-foreground-700 leading-relaxed mb-4">
              从最初的玩法规则整理，到如今涵盖历史数据、号码走势、图库参考等六大知识板块的综合性平台，六合宝典始终坚持内容为王，以专业、准确、及时的信息服务广大玩家。
            </p>
            <p className="text-foreground-700 leading-relaxed">
              我们相信，信息的力量在于帮助人们做出更理性的决策。六合宝典不鼓励盲目投注，而是倡导理性娱乐、科学分析的理念。每一位玩家都应该在充分了解规则和数据的基础上，以健康的心态参与游戏。
            </p>
          </div>
        </section>

        {/* Reputation */}
        <section className="mb-16">
          <h2 className="font-heading text-2xl text-foreground-900 mb-6">
            专业信誉
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div className="bg-background-50 rounded-xl p-6 border border-background-200/70 text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-100">
                <i className="ri-shield-check-line text-xl text-accent-600"></i>
              </div>
              <h3 className="font-heading text-lg text-foreground-900 mb-2">
                数据权威
              </h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                数据来源于官方开奖记录，经过多重校验确保准确性，实时同步更新。
              </p>
            </div>
            <div className="bg-background-50 rounded-xl p-6 border border-background-200/70 text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-100">
                <i className="ri-team-line text-xl text-accent-600"></i>
              </div>
              <h3 className="font-heading text-lg text-foreground-900 mb-2">
                专业团队
              </h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                由资深数据分析师和彩票行业专家组成的核心团队，确保内容质量。
              </p>
            </div>
            <div className="bg-background-50 rounded-xl p-6 border border-background-200/70 text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-100">
                <i className="ri-heart-line text-xl text-accent-600"></i>
              </div>
              <h3 className="font-heading text-lg text-foreground-900 mb-2">
                用户至上
              </h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                始终以用户体验为核心，持续优化内容与服务，打造最贴心的参考平台。
              </p>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="mb-16">
          <h2 className="font-heading text-2xl text-foreground-900 mb-6">
            联系我们
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70">
              <h3 className="font-heading text-lg text-foreground-900 mb-6">
                发送消息
              </h3>

              {formStatus === 'success' ? (
                <div className="text-center py-10">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-check-line text-2xl text-primary-600"></i>
                  </div>
                  <h4 className="font-heading text-lg text-foreground-900 mb-2">
                    提交成功
                  </h4>
                  <p className="text-sm text-foreground-500">
                    感谢您的留言，我们会尽快回复。
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                  data-readdy-form=""
                >
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      姓名
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="subject"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      留言内容
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      placeholder="请输入留言内容（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">
                      最多500个字符
                    </p>
                  </div>
                  <div className="antispam-field">
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    提交留言
                  </button>
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-600 text-center">
                      {formError}
                    </p>
                  )}
                </form>
              )}
            </div>

            <div className="space-y-5">
              <div className="bg-background-50 rounded-xl p-5 border border-background-200/70 flex items-start gap-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                  <i className="ri-mail-line text-lg text-primary-600"></i>
                </div>
                <div>
                  <h4 className="font-medium text-foreground-900 text-sm mb-1">
                    电子邮箱
                  </h4>
                  <p className="text-sm text-foreground-500">
                    contact@liuhebaodian.com
                  </p>
                </div>
              </div>
              <div className="bg-background-50 rounded-xl p-5 border border-background-200/70 flex items-start gap-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                  <i className="ri-customer-service-line text-lg text-primary-600"></i>
                </div>
                <div>
                  <h4 className="font-medium text-foreground-900 text-sm mb-1">
                    客服热线
                  </h4>
                  <p className="text-sm text-foreground-500">
                    400-XXX-XXXX（工作日 9:00-18:00）
                  </p>
                </div>
              </div>
              <div className="bg-background-50 rounded-xl p-5 border border-background-200/70 flex items-start gap-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                  <i className="ri-map-pin-line text-lg text-primary-600"></i>
                </div>
                <div>
                  <h4 className="font-medium text-foreground-900 text-sm mb-1">
                    办公地址
                  </h4>
                  <p className="text-sm text-foreground-500">
                    广东省广州市天河区XXX路XXX号
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}